"use client";

import { useContext, useMemo } from "react";
import { useTranslation, Trans } from "react-i18next";
import Modal, { ModalCloseContext } from "@/shared/ui/components/modal";
import { SmartMembershipIcon } from "@/shared/ui/icons/smart-membership-icon";
import { CloseIcon } from "@/shared/ui/icons/close-icon";
import { CheckIcon } from "@/shared/ui/icons/check-icon";
import { RewardsIcon } from "@/shared/ui/icons/rewards-icon";
import MoneyAmount from "@/shared/ui/components/money-amount";
import Button from "@/shared/ui/components/button";
import { Currency } from "@/types";

interface SmartBenefitsModalProps {
  isOpen: boolean;
  onClose: () => void;
  waivedOffFeeAmount: number;
  cashbackAmount: number;
  rewardAppliedAmount?: number;
  processingFeeAmount?: number;
  currency: Currency | string;
  language: string;
}

interface SmartBenefitsModalContentProps {
  onClose: () => void;
  waivedOffFeeAmount: number;
  cashbackAmount: number;
  rewardAppliedAmount?: number;
  processingFeeAmount?: number;
  currency: Currency | string;
  language: string;
}

function SmartBenefitsModalContent({
  onClose,
  waivedOffFeeAmount,
  cashbackAmount,
  rewardAppliedAmount = 0,
  processingFeeAmount = 0,
  currency,
  language,
}: SmartBenefitsModalContentProps) {
  const { t } = useTranslation();
  const contextClose = useContext(ModalCloseContext);
  const handleClose = contextClose || onClose;

  const hasWaivedOffFee = waivedOffFeeAmount > 0;
  const hasCashback = cashbackAmount > 0;
  const hasRewardApplied = (rewardAppliedAmount ?? 0) > 0;
  const hasSmartBenefits = hasWaivedOffFee || hasCashback;
  const showExclusiveDiscount = hasRewardApplied;
  const showSmartBenefits = hasSmartBenefits;
  const isStandardPartialFee = hasWaivedOffFee && processingFeeAmount > 0;
  const feeAfterWaiverAmount = processingFeeAmount;
  const feeBeforeSmartAmount = waivedOffFeeAmount + processingFeeAmount;
  const showProcessingFeesReducedCopy = isStandardPartialFee;

  // Prepare components for Trans
  const cashbackComponent = useMemo(
    () => (
      <MoneyAmount
        amount={cashbackAmount}
        currency={currency}
        language={language}
        className="inline-flex"
      />
    ),
    [cashbackAmount, currency, language],
  );

  const farahComponent = useMemo(
    () => (
      <MoneyAmount
        amount={rewardAppliedAmount ?? 0}
        currency={currency}
        language={language}
        className="inline-flex"
      />
    ),
    [rewardAppliedAmount, currency, language],
  );

  const reducedFeeComponent = useMemo(
    () => (
      <MoneyAmount
        amount={feeAfterWaiverAmount}
        currency={currency}
        language={language}
        className="inline-flex"
      />
    ),
    [feeAfterWaiverAmount, currency, language],
  );

  const originalFeeComponent = useMemo(
    () => (
      <MoneyAmount
        amount={feeBeforeSmartAmount}
        currency={currency}
        language={language}
        className="inline-flex"
      />
    ),
    [feeBeforeSmartAmount, currency, language],
  );

  const removedProcessingFeeComponent = useMemo(
    () => (
      <MoneyAmount
        amount={waivedOffFeeAmount}
        currency={currency}
        language={language}
        className="inline-flex"
      />
    ),
    [waivedOffFeeAmount, currency, language],
  );

  return (
    <div className="px-6 pb-2 pt-6 space-y-6 overflow-y-auto flex-1 min-h-0 relative">
      <div className="flex items-center gap-6 mb-6">
        <SmartMembershipIcon className="w-[69.96px] h-6" />
      </div>
      <button
        onClick={handleClose}
        className="fixed right-4 top-4 md:right-6 md:top-6 p-2 rounded-full bg-gray-100 z-50 rtl:right-auto rtl:left-4 md:rtl:left-6"
        aria-label="Close"
      >
        <CloseIcon />
      </button>

      {/* Smart Benefits Section */}
      {showSmartBenefits && (
        <>
          <h1
            data-testid="smart-benefits-heading"
            className="text-[#7E82AD] text-xs font-extrabold leading-5 tracking-[0.5px] uppercase mb-2"
            style={{ fontFeatureSettings: "'liga' off" }}
          >
            {t("smart_benefits.title")}
          </h1>
          <div className="flex flex-col items-start gap-1 py-4 px-5 rounded-[20px] bg-[#FBF3FF]">
            {hasWaivedOffFee && (
              <div className="flex items-start gap-2">
                <div className="mr-2 rtl:mr-0 rtl:ml-2">
                  <CheckIcon />
                </div>
                <div className="flex flex-col">
                  <h3 className="text-sm font-medium leading-5 tracking-[-0.21px]">
                    {showProcessingFeesReducedCopy
                      ? t("smart_benefits.fees.partial.title")
                      : t("smart_benefits.fees.title")}
                  </h3>
                  <div className="text-xs leading-5 tracking-[-0.18px] text-[#585B7A] mt-1.5">
                    {showProcessingFeesReducedCopy ? (
                      <Trans
                        i18nKey="smart_benefits.fees.partial.description"
                        components={{
                          reduced_fee: reducedFeeComponent,
                          original_fee: originalFeeComponent,
                        }}
                      />
                    ) : (
                      <Trans
                        i18nKey="smart_benefits.fees.description"
                        components={{
                          removed_fee: removedProcessingFeeComponent,
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}

            {hasWaivedOffFee && hasCashback && (
              <div className="w-[295px] h-[0.5px] bg-[#DAE0EB] mt-4 mb-4" />
            )}

            {hasCashback && (
              <div className="flex items-start gap-2">
                <div className="mr-2 rtl:mr-0 rtl:ml-2">
                  <CheckIcon />
                </div>
                <div className="flex flex-col">
                  <h3 className="text-sm font-medium leading-5 tracking-[-0.21px]">
                    {t("smart_benefits.cashback.title")}
                  </h3>
                  <div className="text-xs leading-5 tracking-[-0.18px] text-[#585B7A] mt-1.5">
                    <Trans
                      i18nKey="smart_benefits.cashback.description"
                      components={{ cashback: cashbackComponent }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Exclusive Discount Section */}
      {showExclusiveDiscount && (
        <>
          <h1
            className="text-[#7E82AD] text-xs font-extrabold leading-5 tracking-[0.5px] uppercase mb-2"
            style={{ fontFeatureSettings: "'liga' off" }}
          >
            {t("exclusive_discount.title")}
          </h1>
          <div className="flex flex-col items-start gap-1 py-4 px-5 rounded-[20px] bg-[#EDFEFD]">
            <div className="flex items-center gap-2">
              <div className="flex items-center mr-2 rtl:mr-0 rtl:ml-2">
                <RewardsIcon className="[&_path]:fill-[#128351]" />
              </div>
              <div className="flex flex-col">
                <h3 className="text-sm font-medium leading-5 tracking-[-0.21px]">
                  {t("exclusive_discount.applied.title")}
                </h3>
                <div className="text-xs leading-5 tracking-[-0.18px] text-[#585B7A] mt-1.5">
                  <Trans
                    i18nKey="exclusive_discount.applied.description"
                    components={{ farah: farahComponent }}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export function SmartBenefitsModal({
  isOpen,
  onClose,
  waivedOffFeeAmount,
  cashbackAmount,
  rewardAppliedAmount,
  processingFeeAmount = 0,
  currency,
  language,
}: SmartBenefitsModalProps) {
  const { t } = useTranslation();

  if (!isOpen) return null;

  return (
    <Modal
      title={t("smart_benefits.title")}
      onClose={onClose}
      showCloseButton={false}
      stickyFooter={(close) => (
        <Button size="large" onClick={close} className="w-full">
          {t("buyer_protection.got_it")}
        </Button>
      )}
    >
      <SmartBenefitsModalContent
        onClose={onClose}
        waivedOffFeeAmount={waivedOffFeeAmount}
        cashbackAmount={cashbackAmount}
        rewardAppliedAmount={rewardAppliedAmount}
        processingFeeAmount={processingFeeAmount}
        currency={currency}
        language={language}
      />
    </Modal>
  );
}
